import { useMemo, type ReactNode } from "react";
import qrcode from "qrcode-generator";
import { phase1Qr, type InvoiceDto, type Phase1QrProblem } from "@delta/shared";
import type { T } from "../i18n";

// The phase 1 QR on a printed sale or sales refund (A4, 80 mm and 58 mm, the POS receipt and the agent's raster):
// the five TLV fields only (seller name, VAT registration number, time stamp, total with VAT, VAT total), made by
// phase1Qr in shared/zatca.ts. It is not a phase 2 e-invoice QR (no hash, signature or key; phase 2 is deferred) and
// has not been validated with ZATCA. When the seller's name or VAT number is missing or invalid, no QR is printed and
// a box says what is wrong instead: Delta never fills in a tax detail.

export interface QrSeller { nameAr: string; nameEn: string; taxNumber: string }

/** Only a posted sale or sales refund carries the QR; a draft has no number and is not issued. */
export const carriesQr = (inv: Pick<InvoiceDto, "kind" | "status">) => (inv.kind === "sale" || inv.kind === "sale_refund") && (inv.status === "completed" || inv.status === "posted");

export const invoiceQr = (s: QrSeller, inv: Pick<InvoiceDto, "invoiceAt" | "grand" | "vat">) =>
  phase1Qr({ sellerNameAr: s.nameAr, sellerNameEn: s.nameEn, vatNumber: s.taxNumber, invoiceAt: inv.invoiceAt, total: inv.grand, vat: inv.vat });

export const qrProblemText = (t: T, problems: Phase1QrProblem[]) => problems.map((p) => t(`qrProblem_${p}`));

/** The QR as SVG modules, with a four-module quiet zone. `mm` sets its printed size. */
export function QrSvg({ text, mm, className }: { text: string; mm: number; className?: string }) {
  const q = useMemo(() => { const x = qrcode(0, "M"); x.addData(text, "Byte"); x.make(); return x; }, [text]);
  const n = q.getModuleCount(), m = 4, cells: ReactNode[] = [];
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (q.isDark(r, c)) cells.push(<rect key={`${r}-${c}`} x={c + m} y={r + m} width={1.02} height={1.02} />);
  return <svg className={className} viewBox={`0 0 ${n + 2 * m} ${n + 2 * m}`} style={{ width: `${mm}mm`, height: `${mm}mm`, background: "#fff", fill: "#000", shapeRendering: "crispEdges" }}
    role="img" aria-label="QR" data-testid="phase1-qr" data-qr={text}>{cells}</svg>;
}

/** The QR and its caption, or the box that says why it is missing. */
export function Phase1Qr({ seller, inv, t, mm, className }: { seller: QrSeller; inv: Pick<InvoiceDto, "invoiceAt" | "grand" | "vat">; t: T; mm: number; className?: string }) {
  const q = invoiceQr(seller, inv);
  if (!q.ok) {
    return (
      <div className={`qr-missing ${className ?? ""}`} role="alert" data-testid="phase1-qr-missing" data-problems={q.problems.join(" ")}>
        <b>{t("qrMissingTitle")}</b>
        <ul>{qrProblemText(t, q.problems).map((x) => <li key={x}>{x}</li>)}</ul>
        <span>{t("qrMissingHint")}</span>
      </div>
    );
  }
  return (
    <div className={`qr-box ${className ?? ""}`} data-testid="phase1-qr-box">
      <QrSvg text={q.text} mm={mm} />
      <div className="qr-note" data-testid="phase1-qr-note">{t("qrPhase1Note")}</div>
    </div>
  );
}
